import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Dropdown } from 'react-bootstrap';
import { ThreeDotsVertical } from 'react-bootstrap-icons';
import config from '../../helpers/config-helper';
import { IActionFormatter } from '../../models/ICommon';
import { formatMessage } from '../../translations/format-message';
import './Grid.css';

const appConfig = config();
const dateTimeFormat = appConfig.REACT_APP_DATE_TIME_FORMAT;

// Shows the cell's full text as a native tooltip on hover - for columns using the
// "show-Elipses" class, where long values are CSS-truncated with "...".
export const TooltipTextFormatter = (cell: any) => (
  <span title={cell || undefined}>{cell}</span>
);

export const ActionFormatter =
  (actions: Array<IActionFormatter>) => (cell: any, row: any) => {
    return (
      <div className="d-flex">
        {actions.map((action: IActionFormatter, index: number) => {
          return (
            <button
              className={`btn btn-light-${action.varient} `}
              key={index}
              title={formatMessage(action.title)}
              type="button"
              disabled={action.isDisable && !row[action.dataField]}
              onClick={(event) => action.handler(row, event)}
            >
              <FontAwesomeIcon icon={action.icon} />
            </button>
          );
        })}
      </div>
    );
  };

export const CustomActionFormatter =
  (actions: Array<IActionFormatter>) => (cell: any, row: any) => {
    return (
      <>
        <div className="d-flex">
          {actions.map((action: IActionFormatter, index: number) => {
            return (
              <button
                className={`btn btn-light-${action.varient} btn-sm`}
                key={index}
                title={formatMessage(action.title)}
                type="button"
                disabled={(action.isDisable && !row[action.dataField]) || (action.title === 'edit' && row?.CurrentState === 'Complete')}
                onClick={(event) => action.handler(row, event)}
                hidden={action.hidden}
              >
                {action.icon}
              </button>
            );
          })}
        </div>
      </>
    );
  };

// Renders row actions as a single "Actions ▾" dropdown instead of a row of icon
// buttons, for grids with too many actions to show inline (e.g. Tool Management).
// popperConfig strategy 'fixed' keeps the menu from being clipped by the grid's
// horizontally-scrolling wrapper (position:fixed escapes overflow clipping from
// ancestors that don't themselves use a transform).
export const DropdownActionFormatter =
  (actions: Array<IActionFormatter>, dividerAfter: string[] = []) =>
  (cell: any, row: any) => {
    const visibleActions = actions.filter((action) => !action.hidden);
    if (visibleActions.length === 0) return null;

    return (
      <Dropdown>
        <Dropdown.Toggle
          className="grid-action-dropdown-toggle"
          variant="light"
          size="sm"
        >
          {formatMessage('actions')}
        </Dropdown.Toggle>
        <Dropdown.Menu
          popperConfig={{ strategy: 'fixed' }}
          renderOnMount
        >
          {visibleActions.map((action, index) => {
            const isDisabled = action.isDisable && !row[action.dataField];
            return (
              <React.Fragment key={index}>
                <Dropdown.Item
                  className={`grid-action-dropdown-item ${action.varient === 'danger' ? 'text-danger' : ''}`}
                  disabled={isDisabled}
                  onClick={(event) => {
                    if (isDisabled) return;
                    action.handler(row, event);
                  }}
                >
                  <span className="grid-action-dropdown-icon">{action.icon}</span>
                  {formatMessage(action.title)}
                </Dropdown.Item>
                {dividerAfter.includes(action.action) && <Dropdown.Divider />}
              </React.Fragment>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

// Renders a small set of "primary" actions (e.g. Edit/Delete) as plain icon buttons —
// the same pattern other grids use (see CustomActionFormatter) — plus a single "More"
// dropdown button (three-dot icon) holding the rest of the row actions, for grids with
// too many actions to show inline as buttons (e.g. Tool Management).
export const IconAndMoreActionFormatter =
  (
    primaryActions: Array<IActionFormatter>,
    moreActions: Array<IActionFormatter>,
    dividerAfter: string[] = []
  ) =>
  (cell: any, row: any) => {
    const visiblePrimary = primaryActions.filter((action) => !action.hidden);
    const visibleMore = moreActions.filter((action) => !action.hidden);
    if (visiblePrimary.length === 0 && visibleMore.length === 0) return null;

    return (
      <div className="d-flex align-items-center grid-row-actions">
        {visiblePrimary.map((action, index) => (
          <button
            className={`btn btn-light-${action.varient} btn-sm`}
            key={index}
            title={formatMessage(action.title)}
            type="button"
            disabled={action.isDisable && !row[action.dataField]}
            onClick={(event) => action.handler(row, event)}
          >
            {action.icon}
          </button>
        ))}
        {visibleMore.length > 0 && (
          <Dropdown>
            <Dropdown.Toggle
              className="grid-action-dropdown-toggle grid-action-more-toggle"
              variant="light"
              size="sm"
              title={formatMessage('more')}
            >
              <ThreeDotsVertical />
            </Dropdown.Toggle>
            <Dropdown.Menu
              popperConfig={{ strategy: 'fixed' }}
              renderOnMount
            >
              {visibleMore.map((action, index) => {
                const isDisabled = action.isDisable && !row[action.dataField];
                return (
                  <React.Fragment key={index}>
                    <Dropdown.Item
                      className={`grid-action-dropdown-item ${action.varient === 'danger' ? 'text-danger' : ''}`}
                      disabled={isDisabled}
                      onClick={(event) => {
                        if (isDisabled) return;
                        action.handler(row, event);
                      }}
                    >
                      <span className="grid-action-dropdown-icon">{action.icon}</span>
                      {formatMessage(action.title)}
                    </Dropdown.Item>
                    {dividerAfter.includes(action.action) &&
                      index < visibleMore.length - 1 && <Dropdown.Divider />}
                  </React.Fragment>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
    );
  };

export const UserReserveButtonFormatter =
  (handler: any) => (cell: any, row: any) => {
    return (
      <div className="form-check form-switch p-0">
        <input
          className="form-check-input ms-0 pointer-events-none"
          type="checkbox"
          title={row.IsReserved ? 'Yes' : 'No'}
          role="switch"
          id="flexSwitchCheckChecked"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handler(row, e);
          }}
          checked={row.IsReserved}
        />
      </div>
    );
  };
